import { useTranslation } from "react-i18next";
import { useApi } from "src/hooks/useApi";
import { formatEuro } from "./StepServices";
import { formatDay } from "./treatmentRules";

export interface SchedulePreview {
    sentDay: string;
    dueDate: string;
    overdueFrom: string;
    levels: { level: number; key: string; date: string | null; fee: number; cumulativeFee: number; sendMode: string }[];
    finesEnabled: boolean;
    legalMode: string;
}

/** Step 5: what happens if the invoice is sent today — dates straight from the nightly jobs' rules
 *  (backend services/manualInvoicePreview.ts). */
export function PreviewSchedule() {
    const { t } = useTranslation();
    const s = (key: string, opts?: Record<string, unknown>) => t(`invoiceCreate.schedule.${key}`, opts);
    const { data, loading, error } = useApi<SchedulePreview>("/invoices/manual/schedule");

    if (loading && !data) return <p className="ic-schedule__hint">{s("loading")}</p>;
    if (error || !data) return <p className="ic-schedule__hint">{s("unavailable")}</p>;

    return (
        <section className="ic-schedule" aria-labelledby="ic-schedule-title">
            <h3 id="ic-schedule-title" className="ic-schedule__title">{s("title")}</h3>
            <ol className="ic-schedule__list">
                <li className="ic-schedule__item">
                    <span className="ic-schedule__date">{formatDay(data.sentDay)}</span>
                    <span className="ic-schedule__what">{s("sent")}</span>
                </li>
                <li className="ic-schedule__item">
                    <span className="ic-schedule__date">{formatDay(data.dueDate)}</span>
                    <span className="ic-schedule__what">{s("due")}</span>
                </li>
                {data.levels.map((l) =>
                    l.date ? (
                        <li key={l.level} className={`ic-schedule__item ic-schedule__item--level${l.level === 4 ? " ic-schedule__item--legal" : ""}`}>
                            <span className="ic-schedule__date">{formatDay(l.date)}</span>
                            <span className="ic-schedule__what">
                                {s(`level${l.level}`)}
                                <span className="ic-schedule__detail">
                                    {l.level === 4
                                        ? s(data.legalMode === "api" ? "legalPaywise" : "legalSelf")
                                        : s(`sendMode.${l.sendMode}`)}
                                    {data.finesEnabled && l.fee > 0 && ` · ${s("fee", { fee: formatEuro(l.fee) })}`}
                                </span>
                            </span>
                        </li>
                    ) : null,
                )}
            </ol>
            <p className="ic-schedule__hint">{s("hint")}</p>
        </section>
    );
}
